import React, { useEffect, useState, useContext, useRef } from "react";

import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import { makeStyles, useTheme, alpha } from "@material-ui/core/styles";
import { CheckCircle, CloudUpload } from "@material-ui/icons";
import { toast } from "react-toastify";

import useSettings from "../../hooks/useSettings";
import OnlyForSuperUser from "../OnlyForSuperUser";
import useAuth from "../../hooks/useAuth.js";
import ColorModeContext from "../../layout/themeContext";
import api from "../../services/api";
import { getBackendUrl } from "../../config";
import { AEROS, aerosGradient } from "../../styles/aerosTokens";
import useAerosSettingsStyles from "./aerosSettingsStyles";

import defaultLogoLight from "../../assets/logo.png";
import defaultLogoDark from "../../assets/logo-black.png";
import defaultLogoFavicon from "../../assets/favicon.ico";
import ColorBoxModal from "../ColorBoxModal/index.js";
import { i18n } from "../../translate/i18n";

// Layout fiel ao mockup "Configurações · Whitelabel" (docs/design/aeros-dark,
// bloco <!-- WHITELABEL -->): grid 2 colunas — esquerda com cards "Identidade"
// (nome + swatches de cor primária) e "Logos e imagens" (slots tracejados);
// direita com "Pré-visualização" (mini-mock da UI) e ações. Abaixo, card
// "Idiomas". Toda a lógica de persistência é a mesma de antes.
const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const border = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  const borderInput = isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)";
  const borderDashed = isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.15)";
  const divider = isLight ? theme.palette.divider : "rgba(255,255,255,0.06)";
  return {
    container: {
      width: "100%",
    },
    grid2Wide: {
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: 14,
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
    },
    column: {
      display: "flex",
      flexDirection: "column",
      gap: 12,
      minWidth: 0,
    },
    fieldGap: {
      marginTop: 10,
    },
    // ---- swatches de cor primária ----
    colorRow: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      flexWrap: "wrap",
    },
    swatch: {
      width: 28,
      height: 28,
      borderRadius: 8,
      border: "2px solid transparent",
      boxSizing: "border-box",
      padding: 0,
      cursor: "pointer",
      flexShrink: 0,
      transition: "transform 0.12s ease",
      "&:hover": { transform: "scale(1.06)" },
      "&:focus-visible": { outline: `2px solid ${alpha(AEROS.cyan, 0.6)}`, outlineOffset: 2 },
    },
    swatchActive: {
      borderColor: AEROS.cyan,
    },
    hexBox: {
      marginLeft: 4,
      backgroundColor: theme.palette.aeros.sidebar,
      border: `1px solid ${borderInput}`,
      borderRadius: 8,
      padding: "5px 9px",
      fontSize: 11.5,
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
      color: theme.palette.text.secondary,
      cursor: "pointer",
      lineHeight: 1.3,
      "&:hover": { borderColor: theme.palette.aeros.borderStrong },
    },
    // ---- slots de logo ----
    slotGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: 8,
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    slot: {
      border: `1px dashed ${borderDashed}`,
      borderRadius: 10,
      padding: 10,
      textAlign: "center",
      cursor: "pointer",
      minWidth: 0,
      transition: "border-color 0.15s ease",
      "&:hover": { borderColor: alpha(theme.palette.primary.main, 0.5) },
      "& .MuiSvgIcon-root": { fontSize: 20, display: "block", margin: "0 auto" },
    },
    slotFilled: {
      backgroundColor: "rgba(52,211,153,0.04)",
    },
    slotIconEmpty: {
      color: theme.palette.text.secondary,
    },
    slotIconFilled: {
      color: theme.palette.success.main,
    },
    slotLabel: {
      fontSize: 11,
      fontWeight: 600,
      marginTop: 4,
      color: theme.palette.text.primary,
    },
    slotHint: {
      fontSize: 10,
      color: theme.palette.aeros.textTertiary,
    },
    slotRemove: {
      marginTop: 6,
      padding: "1px 8px",
      fontSize: 10.5,
      minWidth: 0,
      borderRadius: 7,
    },
    // ---- pré-visualização (mini-mock da UI; tokens do tema atual) ----
    previewCard: {
      display: "flex",
      flexDirection: "column",
    },
    previewFrame: {
      flex: 1,
      backgroundColor: theme.palette.aeros.background,
      border: `1px solid ${theme.palette.aeros.border}`,
      borderRadius: 12,
      overflow: "hidden",
      display: "flex",
      minHeight: 250,
    },
    previewSidebar: {
      width: 78,
      flexShrink: 0,
      backgroundColor: theme.palette.aeros.sidebar,
      borderRight: `1px solid ${divider}`,
      padding: "10px 6px",
    },
    previewLogoBox: {
      width: 22,
      height: 22,
      borderRadius: 6,
      margin: "0 auto 10px",
    },
    previewLogoImg: {
      display: "block",
      maxWidth: 66,
      height: 22,
      objectFit: "contain",
      margin: "0 auto 10px",
    },
    previewBar: {
      height: 6,
      borderRadius: 3,
      margin: "0 4px 6px",
      backgroundColor: alpha(theme.palette.text.primary, 0.08),
    },
    previewBarActive: {
      backgroundColor: alpha(theme.palette.primary.main, 0.3),
    },
    previewMain: {
      flex: 1,
      padding: 12,
      minWidth: 0,
    },
    previewTitleBar: {
      height: 8,
      width: "40%",
      borderRadius: 4,
      backgroundColor: alpha(theme.palette.text.primary, 0.15),
      marginBottom: 10,
    },
    previewCards: {
      display: "grid",
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
      gap: 6,
    },
    previewMiniCard: {
      height: 38,
      borderRadius: 7,
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${border}`,
    },
    previewBlock: {
      marginTop: 8,
      height: 70,
      borderRadius: 7,
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${border}`,
    },
    previewButtonRow: {
      marginTop: 10,
      display: "flex",
      justifyContent: "flex-end",
    },
    previewButton: {
      width: 58,
      height: 18,
      borderRadius: 6,
    },
    previewCaption: {
      fontSize: 10.5,
      color: theme.palette.aeros.textTertiary,
      marginTop: 8,
      textAlign: "center",
    },
    previewActions: {
      display: "flex",
      justifyContent: "flex-end",
      gap: 8,
      marginTop: 12,
    },
    ghostSmall: {
      padding: "7px 12px",
    },
    // ---- idiomas ----
    languagesCard: {
      marginTop: 14,
    },
    languageGrid: {
      display: "flex",
      flexWrap: "wrap",
      gap: 4,
    },
    languageItem: {
      display: "flex",
      alignItems: "center",
      minWidth: 140,
    },
    languageLabel: {
      fontSize: 12.5,
      fontWeight: 500,
      color: theme.palette.text.primary,
    },
    uploadInput: {
      display: "none",
    },
  };
});

const LANGUAGE_OPTIONS = [
  { code: "pt-BR", label: "Português" },
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "ar", label: "عربي" },
];

// Sugestões do mockup (swatches).
const SUGGESTED_COLORS = ["#A044FF", "#22D3EE", "#34D399", "#FBBF24", "#F87171"];

const sameColor = (a, b) =>
  typeof a === "string" && typeof b === "string" && a.toLowerCase() === b.toLowerCase();

export default function Whitelabel(props) {
  const { settings } = props;
  const classes = useStyles();
  const aeros = useAerosSettingsStyles();
  const theme = useTheme();
  const [settingsLoaded, setSettingsLoaded] = useState({});

  const { getCurrentUserInfo } = useAuth();
  const [currentUser, setCurrentUser] = useState({});
  const [loading, setLoading] = useState(true);
  const { colorMode } = useContext(ColorModeContext);
  const [primaryColorLightModalOpen, setPrimaryColorLightModalOpen] =
    useState(false);
  const [primaryColorDarkModalOpen, setPrimaryColorDarkModalOpen] =
    useState(false);

  const logoLightInput = useRef(null);
  const logoDarkInput = useRef(null);
  const logoFaviconInput = useRef(null);
  const backgroundLightInput = useRef(null);
  const backgroundDarkInput = useRef(null);
  const appNameInput = useRef(null);
  const [appName, setAppName] = useState(settingsLoaded.appName || "");
  const [appCopyright, setAppCopyright] = useState("");
  const [enabledLanguages, setEnabledLanguages] = useState(["pt-BR", "en"]);

  const { update } = useSettings();

  function updateSettingsLoaded(key, value) {
    if (
      key === "primaryColorLight" ||
      key === "primaryColorDark" ||
      key === "appName"
    ) {
      localStorage.setItem(key, value);
    }
    const newSettings = { ...settingsLoaded };
    newSettings[key] = value;
    setSettingsLoaded(newSettings);
  }

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    getCurrentUserInfo().then((u) => {
      const userData = u.user || u;
      if (isMounted) {
        setCurrentUser(userData);
      }
    });

    if (Array.isArray(settings) && settings.length) {
      const primaryColorLight = settings.find(
        (s) => s.key === "primaryColorLight"
      )?.value;
      const primaryColorDark = settings.find(
        (s) => s.key === "primaryColorDark"
      )?.value;
      const appLogoLight = settings.find(
        (s) => s.key === "appLogoLight"
      )?.value;
      const appLogoDark = settings.find((s) => s.key === "appLogoDark")?.value;
      const appLogoFavicon = settings.find(
        (s) => s.key === "appLogoFavicon"
      )?.value;
      const appLogoBackgroundLight = settings.find(
        (s) => s.key === "appLogoBackgroundLight"
      )?.value;
      const appLogoBackgroundDark = settings.find(
        (s) => s.key === "appLogoBackgroundDark"
      )?.value;
      const appName = settings.find((s) => s.key === "appName")?.value;
      const enabledLanguagesSetting = settings.find(
        (s) => s.key === "enabledLanguages"
      )?.value;
      let langs = ["pt-BR", "en"];
      try {
        if (enabledLanguagesSetting) {
          langs = JSON.parse(enabledLanguagesSetting);
        }
      } catch { }

      if (isMounted) {
        setAppName(appName || "");
        setAppCopyright(settings.find((s) => s.key === "appCopyright")?.value || "");
        setEnabledLanguages(langs);
        setSettingsLoaded({
          ...settingsLoaded,
          primaryColorLight,
          primaryColorDark,
          appLogoLight,
          appLogoDark,
          appLogoFavicon,
          appLogoBackgroundLight,
          appLogoBackgroundDark,
          appName,
          enabledLanguages: langs,
        });
        setLoading(false);
      }
    }
    else {
      setLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, []);

  async function handleSaveSetting(key, value) {
    await update({
      key,
      value,
    });
    updateSettingsLoaded(key, value);
    toast.success("Operação atualizada com sucesso.");
  }

  async function handleSaveEnabledLanguages(newLangs) {
    await handleSaveSetting("enabledLanguages", newLangs);
    setEnabledLanguages(newLangs);
  }

  const uploadLogo = async (e, mode) => {
    if (!e.target.files) {
      return;
    }

    const file = e.target.files[0];
    const formData = new FormData();

    formData.append("typeArch", "logo");
    formData.append("mode", mode);
    formData.append("file", file);

    await api
      .post("/settings-whitelabel/logo", formData, {
        onUploadProgress: (event) => {
          let progress = Math.round((event.loaded * 100) / event.total);
          console.log(`A imagem está ${progress}% carregada... `);
        },
      })
      .then((response) => {
        updateSettingsLoaded(`appLogo${mode}`, response.data);
        if (mode === "BackgroundLight" || mode === "BackgroundDark") {
        } else {
          colorMode[`setAppLogo${mode}`](
            getBackendUrl() + "/public/" + response.data
          );
        }
      })
      .catch((err) => {
        console.error(`Houve um problema ao realizar o upload da imagem.`);
        console.log(err);
      });
  };

  const handleSaveAppName = async () => {
    await handleSaveSetting("appName", appName);
    colorMode.setAppName(appName || "Pulse");
  };

  const applyPrimaryColorLight = (hex) => {
    handleSaveSetting("primaryColorLight", hex);
    colorMode.setPrimaryColorLight(hex);
  };

  const applyPrimaryColorDark = (hex) => {
    handleSaveSetting("primaryColorDark", hex);
    colorMode.setPrimaryColorDark(hex);
  };

  const handleRestoreDefaults = () => {
    applyPrimaryColorLight(AEROS.defaultPrimary);
    applyPrimaryColorDark(AEROS.defaultPrimary);
  };

  // Tudo já persiste ao alterar; "Salvar" só garante o blur do nome (que
  // dispara o save) ou confirma com o toast.
  const handleSaveAll = () => {
    if (
      appNameInput.current &&
      typeof document !== "undefined" &&
      document.activeElement === appNameInput.current
    ) {
      appNameInput.current.blur();
      return;
    }
    toast.success("Operação atualizada com sucesso.");
  };

  const logoSlots = [
    {
      key: "appLogoLight",
      mode: "Light",
      id: "upload-logo-light-button",
      ref: logoLightInput,
      label: "Logo claro",
      hint: "PNG · 512×128",
      onRemove: () => {
        handleSaveSetting("appLogoLight", "");
        colorMode.setAppLogoLight(defaultLogoLight);
      },
    },
    {
      key: "appLogoDark",
      mode: "Dark",
      id: "upload-logo-dark-button",
      ref: logoDarkInput,
      label: "Logo escuro",
      hint: "PNG · 512×128",
      onRemove: () => {
        handleSaveSetting("appLogoDark", "");
        colorMode.setAppLogoDark(defaultLogoDark);
      },
    },
    {
      key: "appLogoFavicon",
      mode: "Favicon",
      id: "upload-logo-favicon-button",
      ref: logoFaviconInput,
      label: "Favicon",
      hint: "PNG · 64×64",
      onRemove: () => {
        handleSaveSetting("appLogoFavicon", "");
        colorMode.setAppLogoFavicon(defaultLogoFavicon);
      },
    },
    {
      key: "appLogoBackgroundLight",
      mode: "BackgroundLight",
      id: "upload-background-light-button",
      ref: backgroundLightInput,
      label: "Fundo do login (claro)",
      hint: "JPG · 1920×1080",
      onRemove: () => {
        handleSaveSetting("appLogoBackgroundLight", "");
      },
    },
    {
      key: "appLogoBackgroundDark",
      mode: "BackgroundDark",
      id: "upload-background-dark-button",
      ref: backgroundDarkInput,
      label: "Fundo do login (escuro)",
      hint: "JPG · 1920×1080",
      onRemove: () => {
        handleSaveSetting("appLogoBackgroundDark", "");
      },
    },
  ];

  const isLightTheme = theme.palette.type === "light";
  const previewLogoFile = isLightTheme
    ? settingsLoaded.appLogoLight
    : settingsLoaded.appLogoDark;
  const previewLogoUrl = previewLogoFile
    ? getBackendUrl() + "/public/" + previewLogoFile
    : null;
  const previewGradient = aerosGradient(theme.palette.primary.main);

  const renderColorRow = (label, value, onPick, onOpenModal) => (
    <div className={classes.fieldGap}>
      <span className={aeros.fieldLabel}>{label}</span>
      <div className={classes.colorRow}>
        {SUGGESTED_COLORS.map((hex) => (
          <button
            key={hex}
            type="button"
            aria-label={hex}
            title={hex}
            className={`${classes.swatch} ${sameColor(value, hex) ? classes.swatchActive : ""}`}
            style={{ backgroundColor: hex }}
            onClick={() => onPick(hex)}
          />
        ))}
        <div
          role="button"
          tabIndex={0}
          className={classes.hexBox}
          onClick={onOpenModal}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") onOpenModal();
          }}
          title="Escolher outra cor"
        >
          {value ? value.toUpperCase() : "—"}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
        <div>Carregando configurações...</div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <OnlyForSuperUser
        user={currentUser}
        yes={() => (
          <>
            <div className={classes.grid2Wide}>
              {/* Coluna esquerda */}
              <div className={classes.column}>
                {/* IDENTIDADE */}
                <div className={aeros.card}>
                  <div className={aeros.microLabel}>Identidade</div>

                  <label className={aeros.fieldLabel} htmlFor="appname-field">
                    Nome do sistema
                  </label>
                  <TextField
                    id="appname-field"
                    variant="outlined"
                    name="appName"
                    className={aeros.input}
                    value={appName}
                    inputRef={appNameInput}
                    placeholder="Pulse"
                    onChange={(e) => {
                      setAppName(e.target.value);
                    }}
                    onBlur={handleSaveAppName}
                  />

                  <label className={aeros.fieldLabel} htmlFor="appcopyright-field" style={{ marginTop: 10 }}>
                    Copyright
                  </label>
                  <TextField
                    id="appcopyright-field"
                    variant="outlined"
                    name="appCopyright"
                    className={aeros.input}
                    value={appCopyright}
                    placeholder="Aeros Tecnologia"
                    onChange={(e) => setAppCopyright(e.target.value)}
                    onBlur={() => handleSaveSetting("appCopyright", appCopyright)}
                  />

                  {renderColorRow(
                    "Cor primária · tema claro",
                    settingsLoaded.primaryColorLight,
                    applyPrimaryColorLight,
                    () => setPrimaryColorLightModalOpen(true)
                  )}
                  {renderColorRow(
                    "Cor primária · tema escuro",
                    settingsLoaded.primaryColorDark,
                    applyPrimaryColorDark,
                    () => setPrimaryColorDarkModalOpen(true)
                  )}
                </div>

                {/* LOGOS E IMAGENS */}
                <div className={aeros.card}>
                  <div className={aeros.microLabel}>
                    {i18n.t("whitelabel.sections.logos")}
                  </div>
                  <div className={classes.slotGrid}>
                    {logoSlots.map((slot) => {
                      const hasFile = !!settingsLoaded[slot.key];
                      return (
                        <div
                          key={slot.key}
                          role="button"
                          tabIndex={0}
                          className={`${classes.slot} ${hasFile ? classes.slotFilled : ""}`}
                          onClick={() => slot.ref.current && slot.ref.current.click()}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              slot.ref.current && slot.ref.current.click();
                            }
                          }}
                          title={i18n.t("whitelabel.upload")}
                        >
                          {hasFile ? (
                            <CheckCircle className={classes.slotIconFilled} />
                          ) : (
                            <CloudUpload className={classes.slotIconEmpty} />
                          )}
                          <div className={classes.slotLabel}>{slot.label}</div>
                          <div className={classes.slotHint}>{slot.hint}</div>
                          {hasFile ? (
                            <Button
                              size="small"
                              variant="outlined"
                              className={`${aeros.ghostButton} ${classes.slotRemove}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                slot.onRemove();
                              }}
                            >
                              Remover
                            </Button>
                          ) : null}
                          <input
                            type="file"
                            id={slot.id}
                            ref={slot.ref}
                            className={classes.uploadInput}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => uploadLogo(e, slot.mode)}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Coluna direita — PRÉ-VISUALIZAÇÃO */}
              <div className={`${aeros.card} ${classes.previewCard}`}>
                <div className={aeros.microLabel}>Pré-visualização</div>
                <div className={classes.previewFrame}>
                  <div className={classes.previewSidebar}>
                    {previewLogoUrl ? (
                      <img
                        className={classes.previewLogoImg}
                        src={previewLogoUrl}
                        alt={i18n.t("whitelabel.preview")}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    ) : (
                      <div
                        className={classes.previewLogoBox}
                        style={{ background: previewGradient }}
                      />
                    )}
                    <div className={`${classes.previewBar} ${classes.previewBarActive}`} />
                    <div className={classes.previewBar} />
                    <div className={classes.previewBar} />
                    <div className={classes.previewBar} />
                  </div>
                  <div className={classes.previewMain}>
                    <div className={classes.previewTitleBar} />
                    <div className={classes.previewCards}>
                      <div className={classes.previewMiniCard} />
                      <div className={classes.previewMiniCard} />
                      <div className={classes.previewMiniCard} />
                    </div>
                    <div className={classes.previewBlock} />
                    <div className={classes.previewButtonRow}>
                      <div
                        className={classes.previewButton}
                        style={{ background: previewGradient }}
                      />
                    </div>
                  </div>
                </div>
                <div className={classes.previewCaption}>
                  Sidebar, botão primário e destaques seguem a cor escolhida
                </div>
                <div className={classes.previewActions}>
                  <Button
                    variant="outlined"
                    className={`${aeros.ghostButton} ${classes.ghostSmall}`}
                    onClick={handleRestoreDefaults}
                  >
                    Restaurar padrão
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    className={aeros.gradientButtonSmall}
                    onClick={handleSaveAll}
                  >
                    Salvar
                  </Button>
                </div>
              </div>
            </div>

            {/* IDIOMAS */}
            <div className={`${aeros.card} ${classes.languagesCard}`}>
              <div className={aeros.microLabel}>
                {i18n.t("whitelabel.sections.languages")}
              </div>
              <div className={classes.languageGrid}>
                {LANGUAGE_OPTIONS.map((lang) => (
                  <label key={lang.code} className={classes.languageItem}>
                    <Checkbox
                      checked={enabledLanguages.includes(lang.code)}
                      onChange={(e) => {
                        let newLangs = e.target.checked
                          ? [...enabledLanguages, lang.code]
                          : enabledLanguages.filter((c) => c !== lang.code);
                        if (newLangs.length === 0) {
                          toast.error(
                            i18n.t("whitelabel.atLeastOneLanguage")
                          );
                          return;
                        }
                        handleSaveEnabledLanguages(newLangs);
                      }}
                      color="primary"
                      size="small"
                    />
                    <span className={classes.languageLabel}>
                      {lang.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <ColorBoxModal
              open={primaryColorLightModalOpen}
              handleClose={() => setPrimaryColorLightModalOpen(false)}
              onChange={(color) => {
                applyPrimaryColorLight(`#${color.hex}`);
              }}
              currentColor={settingsLoaded.primaryColorLight}
            />
            <ColorBoxModal
              open={primaryColorDarkModalOpen}
              handleClose={() => setPrimaryColorDarkModalOpen(false)}
              onChange={(color) => {
                applyPrimaryColorDark(`#${color.hex}`);
              }}
              currentColor={settingsLoaded.primaryColorDark}
            />
          </>
        )}
      />
    </div>
  );
}
